import { z } from 'zod'

/** Códigos estáveis de erro da API. O web decide texto e comportamento a partir deles. */
export const API_ERROR_CODES = [
  'AUTH_INVALID_CREDENTIALS',
  'AUTH_LOCKED',
  'AUTH_INACTIVE',
  'AUTH_TOKEN_EXPIRED',
  'AUTH_TOKEN_INVALID',
  'AUTH_REQUIRED',
  'VALIDATION_FAILED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'EXPORT_TOO_LARGE',
  'CASE_BLOCKED',
  'CASE_CLOSED',
  'INVALID_TRANSITION',
  'ERP_FIELD_READONLY',
  'INSTALLMENT_IN_AGREEMENT',
  'AGREEMENT_ACTIVE',
  'AGREEMENT_STATUS',
  'AGREEMENT_HAS_PAYMENTS',
  'AGREEMENT_RULES_MISSING',
  'DISCOUNT_ABOVE_LIMIT',
  'SELF_APPROVAL',
  'AMOUNT_ABOVE_BALANCE',
  'AGREEMENT_INSTALLMENT_CLOSED',
  'PAYMENT_STATUS',
  'REVERSAL_WINDOW_CLOSED',
  'INSTALLMENT_NOT_PAID_IN_ERP',
  'UNSUPPORTED_FILE',
  'FILE_TOO_LARGE',
  'TOO_MANY_ROWS',
  'UPLOAD_EXPIRED',
  'MAPPING_PROTECTED',
  'CUSTOMER_EXISTS',
  'CUSTOMER_MERGED',
  'ERP_CONFLICT',
  'ERP_MEMBER_NOT_PRINCIPAL',
  'DUPLICATE_GROUP_STATUS',
  'MERGE_NOT_REVERTIBLE',
  'TEMPLATE_INVALID',
  'TEMPLATE_IN_USE',
  'TEMPLATE_INACTIVE',
  'DUNNING_RULE_INVALID',
  'QUEUE_ITEM_STATUS',
  'QUEUE_DATE_CLOSED',
  'CAMPAIGN_STATUS',
  'CAMPAIGN_EMPTY',
  'PUSH_DISABLED',
  'STREAM_TICKET_INVALID',
  'REMINDER_STATUS',
  'DRILL_NOT_AVAILABLE',
  'DRILL_EXPORT_TOO_LARGE',
  'ROLE_GUARD',
  'ROLE_IN_USE',
  'ROLE_SYSTEM',
  'USER_SELF_ACTION',
  'LAST_ADMIN',
  'PASSWORD_POLICY',
  'INVITE_INVALID',
  'COLLECTOR_HAS_OPEN_CASES',
  'COLLECTOR_USER_TAKEN',
  'UNIT_IN_USE',
  'SETTING_UNKNOWN',
  'MAIL_NOT_CONFIGURED',
  'PAYLOAD_TOO_LARGE',
  'CONSENT_DENIED',
  'CUSTOMER_ANONYMIZED',
  'ANONYMIZE_BLOCKED',
  'LGPD_EXPORT_EXPIRED',
  'LGPD_REQUEST_STATUS',
  'LEAD_CHANGED',
  'LOST_REASON_REQUIRED',
  'LEAD_ALREADY_WON',
  'STAGE_INACTIVE',
  'STAGE_HAS_LEADS',
  'PIPELINE_INVALID',
  'LEAD_REOPEN_BLOCKED',
  'LEAD_ANONYMIZED',
  'FINANCE_PERIOD_CLOSED',
  'FINANCE_PERIOD_STATUS',
  'FINANCE_ENTRY_READONLY',
  'FINANCE_ENTRY_STATUS',
  'FINANCE_SETTLEMENT_REVERSED',
  'FINANCE_CATEGORY_TYPE',
  'FINANCE_IN_USE',
  'BANK_ACCOUNT_REQUIRED',
  'BANK_ACCOUNT_INACTIVE',
  'STATEMENT_PARSE_FAILED',
  'STATEMENT_LINE_STATUS',
  'STATEMENT_MATCH_INVALID',
  'REPORT_INVALID',
  'REPORT_TIMEOUT',
  'REPORT_TOO_LARGE',
  'REPORT_FORBIDDEN_DATASET',
  'REPORT_PDF_UNAVAILABLE',
  'REPORT_PDF_FAILED',
  'REPORT_SYSTEM_READONLY',
  'REPORT_RECIPIENTS_INVALID',
  'INTERNAL_ERROR',
] as const

export type ApiErrorCode = (typeof API_ERROR_CODES)[number]

export const ApiErrorCodeSchema = z.enum(API_ERROR_CODES)

/** Mensagens padrão em pt-BR: explicam e orientam, nunca pedem desculpa. */
export const API_ERROR_MESSAGES: Readonly<Record<ApiErrorCode, string>> = Object.freeze({
  AUTH_INVALID_CREDENTIALS:
    "E-mail ou senha incorretos. Tente de novo ou use 'Esqueci minha senha'.",
  AUTH_LOCKED:
    "Acesso bloqueado por 15 minutos depois de várias tentativas. Aguarde ou use 'Esqueci minha senha'.",
  AUTH_INACTIVE: 'Este usuário está desativado. Fale com a coordenação para reativar.',
  AUTH_TOKEN_EXPIRED: 'Sua sessão expirou. Entre de novo.',
  AUTH_TOKEN_INVALID: "Este link não vale mais. Peça um novo em 'Esqueci minha senha'.",
  AUTH_REQUIRED: 'Entre para continuar.',
  VALIDATION_FAILED: 'Revise os campos destacados e envie de novo.',
  FORBIDDEN: 'Seu perfil não tem acesso a esta ação.',
  NOT_FOUND: 'Não encontramos o que você procurou.',
  CONFLICT: 'Já existe um registro com esses dados.',
  RATE_LIMITED: 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.',
  EXPORT_TOO_LARGE:
    'A exportação passa de 50.000 linhas. Filtre a carteira para menos casos e exporte de novo.',
  CASE_BLOCKED:
    'Este caso está bloqueado e não aceita atendimento. Veja o motivo na ficha antes de seguir.',
  CASE_CLOSED: 'Este caso já foi encerrado. Abra a ficha para ver o caso atual do cliente.',
  INVALID_TRANSITION: 'Esse status não pode vir depois do status atual. Escolha uma das opções.',
  ERP_FIELD_READONLY: 'Este dado vem do ERP e só muda lá. Cadastre um novo contato no CRM.',
  INSTALLMENT_IN_AGREEMENT:
    'Uma das parcelas já está em outro acordo ativo. Tire a parcela da seleção ou encerre o outro acordo.',
  AGREEMENT_ACTIVE:
    'Este caso já tem um acordo ativo. Cancele ou quebre o acordo atual antes de criar outro.',
  AGREEMENT_STATUS: 'O status atual do acordo não permite esta ação.',
  AGREEMENT_HAS_PAYMENTS:
    'O acordo já tem pagamento registrado. Estorne os pagamentos ou quebre o acordo.',
  AGREEMENT_RULES_MISSING:
    'As regras de negociação não estão configuradas. Fale com a administração.',
  DISCOUNT_ABOVE_LIMIT:
    'O desconto passa do seu limite de aprovação. Peça para a diretoria aprovar.',
  SELF_APPROVAL: 'Quem criou o acordo não pode aprová-lo. Peça para outra pessoa da coordenação.',
  AMOUNT_ABOVE_BALANCE:
    'O valor passa do saldo da parcela. Confira o valor ou escolha outra parcela.',
  AGREEMENT_INSTALLMENT_CLOSED: 'Esta parcela do acordo já está paga ou cancelada.',
  PAYMENT_STATUS: 'O status atual do pagamento não permite esta ação.',
  REVERSAL_WINDOW_CLOSED:
    'O prazo para você estornar este pagamento acabou. Peça o estorno ao financeiro.',
  INSTALLMENT_NOT_PAID_IN_ERP:
    'Esta parcela ainda não tem baixa no ERP. Aguarde a próxima carga para conciliar.',
  UNSUPPORTED_FILE:
    'Formato não aceito. Envie .xlsx, .csv, .tsv ou .txt; planilhas .xls e .ods: salve como .xlsx.',
  FILE_TOO_LARGE: 'O arquivo passa de 20 MB. Divida a planilha em partes menores.',
  TOO_MANY_ROWS: 'A carga passa de 50.000 linhas. Divida a planilha em partes menores.',
  UPLOAD_EXPIRED: 'O arquivo enviado não está mais disponível. Envie a planilha de novo.',
  MAPPING_PROTECTED: 'Este mapeamento é padrão do sistema e não pode ser apagado.',
  CUSTOMER_EXISTS:
    'Já existe um cliente com este CPF. Abra a ficha dele em vez de cadastrar outro.',
  CUSTOMER_MERGED: 'Este cadastro foi mesclado em outro cliente.',
  ERP_CONFLICT:
    'Os dois cadastros vêm do ERP. Corrija a duplicidade na origem; aqui o grupo só pode ser descartado.',
  ERP_MEMBER_NOT_PRINCIPAL: 'O cliente do ERP precisa ser o principal da mesclagem.',
  DUPLICATE_GROUP_STATUS: 'Este grupo de duplicidade já foi mesclado ou descartado.',
  MERGE_NOT_REVERTIBLE:
    'A mesclagem não pode ser revertida: houve pagamento novo em registros movidos depois dela.',
  TEMPLATE_INVALID:
    'O texto do modelo de mensagem tem problemas. Corrija os itens listados e salve de novo.',
  TEMPLATE_IN_USE:
    'Este modelo de mensagem é usado por uma etapa ativa da régua. Troque o modelo da etapa antes de desativar.',
  TEMPLATE_INACTIVE:
    'Este modelo de mensagem está inativo ou é de outro canal. Escolha um modelo ativo do canal.',
  DUNNING_RULE_INVALID: 'A régua tem etapas inválidas. Corrija os itens listados e salve de novo.',
  QUEUE_ITEM_STATUS: 'O status atual do item da fila não permite esta ação.',
  QUEUE_DATE_CLOSED: 'A fila de um dia passado é só leitura.',
  CAMPAIGN_STATUS: 'O status atual da campanha não permite esta ação.',
  CAMPAIGN_EMPTY: 'Nenhum caso do público pode receber esta campanha. Revise o filtro ou o canal.',
  PUSH_DISABLED:
    'O push está desligado no servidor. As notificações continuam chegando no sino com a aba aberta.',
  STREAM_TICKET_INVALID: 'A conexão de tempo real expirou. Reconectando.',
  REMINDER_STATUS: 'O status atual do lembrete não permite esta ação.',
  DRILL_NOT_AVAILABLE:
    'Este número não tem lista própria. Abra o detalhamento de cada parte do cálculo.',
  DRILL_EXPORT_TOO_LARGE:
    'O detalhamento passa de 50.000 linhas. Filtre o painel por período, unidade ou cobradora e exporte de novo.',
  ROLE_GUARD:
    'Esta mudança tiraria uma permissão protegida. Veja os itens travados e ajuste a seleção.',
  ROLE_IN_USE: 'O perfil tem usuários. Mude o perfil deles antes de apagar.',
  ROLE_SYSTEM:
    'Perfis do sistema não podem ser apagados. Ajuste as permissões ou restaure o padrão.',
  USER_SELF_ACTION:
    'Você não pode fazer isso com o seu próprio usuário. Peça para outra pessoa da administração.',
  LAST_ADMIN:
    'Este é o último usuário ativo com acesso à administração de usuários. Dê o acesso a outra pessoa antes.',
  PASSWORD_POLICY: 'A senha não atende à política de segurança. Revise os itens listados.',
  INVITE_INVALID: 'Este convite não vale mais. Peça um novo convite à administração.',
  COLLECTOR_HAS_OPEN_CASES:
    'A cobradora tem casos abertos. Distribua ou reatribua os casos antes de desativar.',
  COLLECTOR_USER_TAKEN:
    'Este usuário já está vinculado a outra cobradora. Desfaça o outro vínculo antes.',
  UNIT_IN_USE:
    'A unidade tem cobradoras ou usuários ativos. Mude o vínculo deles antes de desativar.',
  SETTING_UNKNOWN: 'Este parâmetro não pode ser editado pela tela.',
  MAIL_NOT_CONFIGURED:
    'O envio de e-mail não está configurado no servidor. Use o link para copiar.',
  CONSENT_DENIED:
    'O titular negou contato por este canal. Use outro canal ou registre a mudança na ficha.',
  CUSTOMER_ANONYMIZED:
    'Este titular foi anonimizado. Os dados pessoais não podem mais ser alterados.',
  ANONYMIZE_BLOCKED:
    'O titular tem caso aberto ou acordo ativo. Só a diretoria pode anonimizar, com justificativa.',
  LGPD_EXPORT_EXPIRED: 'O arquivo desta exportação expirou. Peça uma nova exportação.',
  LGPD_REQUEST_STATUS: 'O status atual da solicitação não permite esta ação.',
  PAYLOAD_TOO_LARGE: 'O envio passou do tamanho permitido. Encurte e tente de novo.',
  LEAD_CHANGED:
    'Outra pessoa mudou este lead enquanto você editava. Confira a versão atual e tente de novo.',
  LOST_REASON_REQUIRED: 'Escolha o motivo da perda para mover o lead para Perdido.',
  LEAD_ALREADY_WON: 'Este lead já foi ganho. Reabra o lead para mudar a etapa.',
  STAGE_INACTIVE: 'Esta etapa está desativada. Escolha outra etapa do funil.',
  STAGE_HAS_LEADS:
    'Há leads nesta etapa. Desative a etapa e escolha para onde os leads vão, em vez de apagar.',
  PIPELINE_INVALID: 'O funil precisa de uma etapa Ganho, uma Perdido e ao menos uma em andamento.',
  LEAD_REOPEN_BLOCKED:
    'O cliente deste lead já tem caso de cobrança. O lead não pode ser reaberto.',
  LEAD_ANONYMIZED: 'Este lead foi anonimizado e não aceita mais alterações.',
  FINANCE_PERIOD_CLOSED:
    'O mês está fechado no financeiro. Lance o ajuste com data no mês aberto ou peça a reabertura.',
  FINANCE_PERIOD_STATUS: 'O mês já está nesta situação. Atualize a tela e confira.',
  FINANCE_ENTRY_READONLY: 'Esta linha vem do ERP ou da cobrança e é somente leitura no financeiro.',
  FINANCE_ENTRY_STATUS: 'A situação atual do lançamento não permite esta ação.',
  FINANCE_SETTLEMENT_REVERSED: 'Esta baixa já foi estornada.',
  FINANCE_CATEGORY_TYPE:
    'A categoria não combina com o tipo do lançamento. Escolha uma categoria de receita ou de despesa conforme o caso.',
  FINANCE_IN_USE: 'O cadastro tem lançamentos. Desative em vez de apagar.',
  BANK_ACCOUNT_REQUIRED: 'Escolha a conta bancária em que o valor entrou ou saiu.',
  BANK_ACCOUNT_INACTIVE: 'A conta bancária está desativada. Escolha uma conta ativa.',
  STATEMENT_PARSE_FAILED:
    'Não foi possível ler o extrato. Envie um OFX ou um CSV com data, valor e descrição.',
  STATEMENT_LINE_STATUS: 'A situação atual da linha do extrato não permite esta ação.',
  STATEMENT_MATCH_INVALID:
    'O lançamento escolhido não casa com a linha do extrato: confira valor, conta e data.',
  REPORT_INVALID: 'O relatório tem campos a revisar. Veja os itens destacados.',
  REPORT_TIMEOUT: 'O relatório ficou pesado demais; filtre um período menor.',
  REPORT_TOO_LARGE:
    'O detalhamento passa de 50.000 linhas. Filtre o relatório para menos registros e exporte de novo.',
  REPORT_FORBIDDEN_DATASET: 'Seu perfil não tem acesso a este conjunto de dados.',
  REPORT_PDF_UNAVAILABLE:
    'A geração de PDF não está disponível neste servidor (navegador não encontrado). Exporte em Excel ou CSV.',
  REPORT_PDF_FAILED:
    'Não foi possível gerar o PDF. Filtre um período menor ou tire a tabela e tente de novo.',
  REPORT_SYSTEM_READONLY:
    'Itens da biblioteca do sistema não podem ser alterados. Duplique para editar.',
  REPORT_RECIPIENTS_INVALID:
    'Alguns destinatários não podem receber este envio. Veja a lista e tire quem não pode.',
  INTERNAL_ERROR: 'Não foi possível concluir agora. Tente de novo em instantes.',
})

export const ValidationIssueSchema = z.object({
  path: z.string(),
  message: z.string(),
})
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>

export const ApiErrorSchema = z.object({
  statusCode: z.number().int(),
  code: ApiErrorCodeSchema,
  message: z.string(),
  details: z.unknown().optional(),
})
export type ApiError = z.infer<typeof ApiErrorSchema>

/** Converte um erro do zod na lista `details` de um `VALIDATION_FAILED`. */
export function toValidationIssues(error: z.ZodError): ValidationIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    message: issue.message,
  }))
}
